<template>
  <div>
    <CustomDialog
      v-model="open"
      width="1000px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="waste-dialog"
      :close-btn="true"
      :footer="!!(waste && showActions)"
      :body-padding-top="false"
      @cancel="open = false"
    >
      <template #title-content>
        <div class="wd-header-content">
          <div class="wd-header-icon-box">
            <v-icon size="18" color="primary">fal fa-trash-alt</v-icon>
          </div>
          <div v-if="waste" class="wd-header-text">
            <div class="wd-header-eyebrow">{{ $t("waste_detail_title") }}</div>
            <div class="wd-header-code-row">
              <span class="wd-header-code">{{ waste.code }}</span>
              <div class="wd-header-badges">
                <WasteStatusPill :status="waste.status" />
                <span class="wd-pill-doc">{{ docTypeLabel(waste.doc_type) }}</span>
              </div>
            </div>
          </div>
          <v-skeleton-loader v-else type="heading" class="wd-header-skeleton" />
        </div>
      </template>

      <template #activator>
        <div class="waste-detail-content">
          <div v-if="loading || !waste" aria-busy="true" class="py-4">
            <v-skeleton-loader type="article, table-tbody, actions" />
          </div>

          <template v-else>
            <!-- Banner Alerts / Notices -->
            <div v-if="waste.reject_reason" class="wd-alert wd-alert--error">
              <div class="wd-alert__icon"><v-icon small color="error">fal fa-exclamation-circle</v-icon></div>
              <div class="wd-alert__body">
                <strong>{{ $t("reject_reason") }}:</strong>
                <span>{{ waste.reject_reason }}</span>
              </div>
            </div>

            <div v-if="waste.status === 'cancelled' && waste.cancel_reason" class="wd-alert wd-alert--warning">
              <div class="wd-alert__icon"><v-icon small color="warning">fal fa-ban</v-icon></div>
              <div class="wd-alert__body">
                <strong>{{ $t("waste_cancel_reason") }}:</strong>
                <span>{{ waste.cancel_reason }}</span>
              </div>
            </div>

            <div v-if="waste.original_waste" class="wd-alert wd-alert--info">
              <div class="wd-alert__icon"><v-icon small color="primary">fal fa-link</v-icon></div>
              <div class="wd-alert__body">
                <strong>{{ $t("waste_original_document") }}:</strong>
                <span>{{ waste.original_waste.code }}</span>
              </div>
            </div>

            <div v-if="waste.reversal" class="wd-alert wd-alert--neutral">
              <div class="wd-alert__icon"><v-icon small color="#64748b">fal fa-undo</v-icon></div>
              <div class="wd-alert__body">
                <strong>{{ $t("waste_reversal_document") }}:</strong>
                <span>{{ waste.reversal.code }}</span>
              </div>
            </div>

            <!-- KPI Metric Summary -->
            <div class="wd-kpis">
              <div class="wd-kpi-card">
                <div class="wd-kpi-card__icon wd-kpi-card__icon--indigo">
                  <v-icon size="18">fal fa-boxes</v-icon>
                </div>
                <div class="wd-kpi-card__content">
                  <div class="wd-kpi-card__label">{{ $t("items") }}</div>
                  <div class="wd-kpi-card__val">{{ waste.total_items }}</div>
                </div>
              </div>

              <div class="wd-kpi-card">
                <div class="wd-kpi-card__icon wd-kpi-card__icon--rose">
                  <v-icon size="18">fal fa-coins</v-icon>
                </div>
                <div class="wd-kpi-card__content">
                  <div class="wd-kpi-card__label">{{ $t("waste_cost_impact") }}</div>
                  <div class="wd-kpi-card__val text-rose">{{ wasteNumber(waste.total_cost) }}</div>
                </div>
              </div>

              <div class="wd-kpi-card">
                <div class="wd-kpi-card__icon wd-kpi-card__icon--amber">
                  <v-icon size="18">fal fa-tags</v-icon>
                </div>
                <div class="wd-kpi-card__content">
                  <div class="wd-kpi-card__label">{{ $t("waste_lost_sale_value") }}</div>
                  <div class="wd-kpi-card__val text-amber">{{ wasteNumber(waste.total_sale_value) }}</div>
                </div>
              </div>
            </div>

            <!-- Grid: Timeline & Details -->
            <div class="wd-two-col-grid">
              <!-- Timeline Card -->
              <section class="wd-panel">
                <div class="wd-panel__header">
                  <v-icon small class="mr-2" color="primary">fal fa-history</v-icon>
                  <span class="wd-panel__title">{{ $t("waste_timeline") }}</span>
                </div>
                <div class="wd-panel__body">
                  <ol class="wd-timeline">
                    <li
                      v-for="event in timeline"
                      :key="event.key"
                      class="wd-timeline__item"
                      :class="[`is-${event.tone}`, event.current ? 'is-current' : 'is-done']"
                      :aria-current="event.current ? 'step' : null"
                    >
                      <span class="wd-timeline__dot" :style="dotStyle(event)" aria-hidden="true">
                        <svg
                          v-if="event.key !== 'waiting'"
                          class="wd-timeline__tick"
                          viewBox="0 0 12 12"
                          width="10"
                          height="10"
                        >
                          <path
                            d="M2.4 6.4l2.3 2.3 4.9-5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                      <div class="wd-timeline__content">
                        <div class="wd-timeline__title-row">
                          <span class="wd-timeline__title">{{ event.title }}</span>
                          <span v-if="event.current" class="wd-timeline__badge">
                            {{ $t("waste_timeline_current") }}
                          </span>
                        </div>
                        <div class="wd-timeline__meta">
                          <span v-if="event.who" class="font-weight-medium">{{ event.who }}</span>
                          <span v-if="event.who && event.at" class="mx-1 text-slate-400">•</span>
                          <span v-if="event.at" class="text-slate-500">{{ event.at }}</span>
                        </div>
                      </div>
                    </li>
                  </ol>
                </div>
              </section>

              <!-- Document Details Card -->
              <section class="wd-panel">
                <div class="wd-panel__header">
                  <v-icon small class="mr-2" color="primary">fal fa-info-circle</v-icon>
                  <span class="wd-panel__title">{{ $t("waste_info") }}</span>
                </div>
                <div class="wd-panel__body">
                  <dl class="wd-info-list">
                    <div class="wd-info-item">
                      <dt class="wd-info-item__label">{{ $t("requested_by") }}</dt>
                      <dd class="wd-info-item__val">
                        <v-icon x-small color="#64748b" class="mr-1">fal fa-user</v-icon>
                        {{ wasteUserName(waste.requested_by) }}
                      </dd>
                    </div>
                    <div class="wd-info-item">
                      <dt class="wd-info-item__label">{{ $t("date") }}</dt>
                      <dd class="wd-info-item__val">
                        <v-icon x-small color="#64748b" class="mr-1">fal fa-calendar-alt</v-icon>
                        {{ wasteDate(waste.date, false) }}
                      </dd>
                    </div>
                    <div v-if="waste.note" class="wd-info-item wd-info-item--full">
                      <dt class="wd-info-item__label">{{ $t("note") }}</dt>
                      <dd class="wd-info-item__note">{{ waste.note }}</dd>
                    </div>
                  </dl>
                </div>
              </section>
            </div>

            <!-- Photos / Evidence Section -->
            <section class="wd-section">
              <div class="wd-section__head">
                <div class="wd-section__title-group">
                  <v-icon small color="primary" class="mr-2">fal fa-images</v-icon>
                  <span class="wd-section__title">{{ $t("waste_evidence") }}</span>
                  <span class="wd-counter-badge">{{ waste.images.length }}</span>
                </div>
                <v-btn
                  v-if="canEditPhotos"
                  small
                  outlined
                  rounded
                  color="primary"
                  class="text-none font-weight-bold"
                  :loading="uploading"
                  @click="$refs.photoInput.click()"
                >
                  <v-icon left x-small>fal fa-plus</v-icon>
                  {{ $t("waste_add_photos") }}
                </v-btn>
                <input
                  ref="photoInput"
                  type="file"
                  class="wd-hidden-input"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  @change="onAddPhotos"
                />
              </div>

              <div v-if="waste.images.length" class="wd-gallery">
                <div v-for="image in waste.images" :key="image.id" class="wd-gallery__item">
                  <button
                    type="button"
                    class="wd-gallery__open"
                    :aria-label="$t('view') + ': ' + (image.file_name || '')"
                    @click="preview(image)"
                  >
                    <img
                      :src="wasteImageSrc(image.file_path)"
                      :alt="image.file_name || ''"
                      loading="lazy"
                    />
                    <div class="wd-gallery__hover-overlay">
                      <v-icon small color="white">fal fa-search-plus</v-icon>
                    </div>
                  </button>
                  <button
                    v-if="canEditPhotos && waste.images.length > 1"
                    type="button"
                    class="wd-gallery__remove"
                    :aria-label="$t('waste_remove_photo')"
                    @click="removePhoto(image)"
                  >
                    <v-icon x-small color="error">fal fa-trash-alt</v-icon>
                  </button>
                </div>
              </div>
              <div v-else class="wd-empty-box">
                <v-icon size="32" color="#94a3b8" class="mb-2">fal fa-image-polaroid</v-icon>
                <span>{{ $t("no_data") }}</span>
              </div>

              <div v-if="photoError" class="wd-alert wd-alert--error mt-2" role="alert">
                <v-icon small color="error" class="mr-2">fal fa-exclamation-triangle</v-icon>
                <span>{{ photoError }}</span>
              </div>
            </section>

            <!-- Line Items List -->
            <section class="wd-section">
              <div class="wd-section__head">
                <div class="wd-section__title-group">
                  <v-icon small color="primary" class="mr-2">fal fa-list-alt</v-icon>
                  <span class="wd-section__title">{{ $t("waste_lines") }}</span>
                  <span class="wd-counter-badge">{{ waste.details.length }}</span>
                </div>
              </div>

              <div class="wd-lines-container">
                <article
                  v-for="line in waste.details"
                  :key="line.id"
                  class="wd-line-card"
                  :class="`wd-line-card--${line.item_type}`"
                >
                  <header class="wd-line-card__header">
                    <div class="wd-line-card__icon-badge">
                      <v-icon size="16">{{ typeIcon(line.item_type) }}</v-icon>
                    </div>
                    <div class="wd-line-card__title-wrap">
                      <div class="wd-line-card__name-row">
                        <span class="wd-line-card__name">{{ line.product_name || "—" }}</span>
                        <span v-if="line.variant_name" class="wd-chip-variant">
                          {{ line.variant_name }}
                        </span>
                      </div>
                      <div class="wd-line-card__tags">
                        <WasteItemTypeTag :type="line.item_type" />
                        <span class="wd-chip-tag">{{ wasteTypeLabel(line.waste_type) }}</span>
                        <span v-if="line.direction === 'in'" class="wd-chip-tag wd-chip-tag--in">
                          <v-icon x-small color="success" class="mr-1">fal fa-arrow-down</v-icon>
                          {{ $t("waste_direction_in") }}
                        </span>
                        <span v-if="line.sku" class="wd-chip-sku">
                          <v-icon x-small color="#94a3b8" class="mr-1">fal fa-barcode</v-icon>
                          {{ line.sku }}
                        </span>
                      </div>
                    </div>

                    <div class="wd-line-card__cost-block">
                      <div class="wd-line-card__cost-amount">
                        {{ line.direction === "in" ? "−" : "" }}{{ wasteNumber(line.total_cost) }}
                      </div>
                      <div class="wd-line-card__cost-caption">{{ $t("waste_col_total_cost") }}</div>
                    </div>
                  </header>

                  <div class="wd-line-metrics">
                    <div class="wd-metric-col">
                      <div class="wd-metric-label">{{ $t("quantity") }}</div>
                      <div
                        class="wd-metric-val font-weight-bold"
                        :class="line.direction === 'in' ? 'text-success' : 'text-slate-800'"
                      >
                        {{ line.direction === "in" ? "+" : "−" }}{{ wasteNumber(line.qty, 3) }}
                        <span class="wd-metric-unit">{{ unitName(line.unit) }}</span>
                      </div>
                      <div v-if="line.recovered_qty > 0" class="wd-metric-sub">
                        {{ $t("waste_recovered_qty") }}: {{ wasteNumber(line.recovered_qty, 3) }}
                      </div>
                    </div>

                    <div class="wd-metric-col">
                      <div class="wd-metric-label">{{ $t("waste_col_stock_change") }}</div>
                      <div class="wd-metric-val">
                        <span class="text-slate-600">{{ wasteNumber(line.last_stock, 3) }}</span>
                        <template v-if="stockAfter(line).value !== null">
                          <v-icon x-small color="#94a3b8" class="mx-1">fal fa-arrow-right</v-icon>
                          <strong :class="{ 'wd-estimate-text': stockAfter(line).estimate }">
                            {{ wasteNumber(stockAfter(line).value, 3) }}
                          </strong>
                        </template>
                        <span class="wd-metric-unit">{{ unitName(line.unit) }}</span>
                      </div>
                      <div v-if="stockAfter(line).estimate" class="wd-metric-sub text-amber">
                        <v-icon x-small color="#f59e0b" class="mr-1">fal fa-hourglass-half</v-icon>
                        {{ $t("waste_estimate_tag") }}
                      </div>
                    </div>

                    <div class="wd-metric-col">
                      <div class="wd-metric-label">{{ $t("waste_unit_cost") }}</div>
                      <div class="wd-metric-val font-weight-medium">
                        {{ wasteNumber(line.unit_cost) }}
                      </div>
                    </div>

                    <div v-if="line.lot_no" class="wd-metric-col">
                      <div class="wd-metric-label">{{ $t("waste_lot_no") }}</div>
                      <div class="wd-metric-val">{{ line.lot_no }}</div>
                    </div>

                    <div v-if="line.expired_date" class="wd-metric-col">
                      <div class="wd-metric-label">{{ $t("waste_expired_date") }}</div>
                      <div class="wd-metric-val">{{ wasteDate(line.expired_date, false) }}</div>
                    </div>
                  </div>

                  <div v-if="line.reason || line.note" class="wd-line-comment">
                    <v-icon x-small color="#64748b" class="mr-1">fal fa-comment-alt-lines</v-icon>
                    <span v-if="line.reason" class="font-weight-medium">{{ line.reason }}</span>
                    <span v-if="line.reason && line.note" class="mx-2 text-slate-300">•</span>
                    <span v-if="line.note" class="text-slate-500">{{ line.note }}</span>
                  </div>

                  <template v-if="line.ingredient_lines && line.ingredient_lines.length">
                    <button
                      type="button"
                      class="wd-breakdown-toggle"
                      :aria-expanded="String(!!expanded[line.id])"
                      @click="$set(expanded, line.id, !expanded[line.id])"
                    >
                      <v-icon x-small class="mr-1" color="primary">
                        {{ expanded[line.id] ? "fal fa-chevron-up" : "fal fa-chevron-down" }}
                      </v-icon>
                      <span>{{ $t("waste_recipe_breakdown") }}</span>
                      <span class="wd-counter-badge ml-2">{{ line.ingredient_lines.length }}</span>
                    </button>

                    <v-expand-transition>
                      <div v-show="expanded[line.id]" class="wd-breakdown-table">
                        <div
                          v-for="ing in line.ingredient_lines"
                          :key="ing.id"
                          class="wd-breakdown-table__row"
                        >
                          <span class="wd-breakdown-table__name">{{ ing.name || "—" }}</span>
                          <span class="wd-breakdown-table__stock">
                            {{ wasteNumber(ing.pre_stock, 3) }} → {{ wasteNumber(ing.post_stock, 3) }}
                          </span>
                          <span class="wd-breakdown-table__qty">
                            {{ ing.qty_change > 0 ? "+" : "" }}{{ wasteNumber(ing.qty_change, 3) }}
                            <span class="wd-metric-unit">{{ unitName(ing.unit) }}</span>
                          </span>
                          <span class="wd-breakdown-table__cost font-weight-bold">
                            {{ wasteNumber(ing.total_cost) }}
                          </span>
                        </div>
                      </div>
                    </v-expand-transition>
                  </template>
                </article>
              </div>
            </section>
          </template>
        </div>
      </template>

      <!-- Custom Footer Actions -->
      <template #actions>
        <div v-if="waste && showActions" class="wd-footer-bar">
          <template v-if="mode">
            <div class="wd-footer-reason-mode">
              <v-textarea
                v-model="reasonText"
                :label="mode === 'reject' ? $t('reject_reason') : $t('waste_cancel_reason')"
                outlined
                dense
                rows="2"
                auto-grow
                hide-details
                maxlength="500"
                :error="reasonError"
                class="wd-footer-textarea flex-grow-1"
              />
              <div class="wd-footer-btn-group ml-auto">
                <v-btn
                  text
                  rounded
                  class="text-none font-weight-bold wd-btn-cancel"
                  :disabled="acting"
                  @click="mode = null"
                >
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn
                  :color="mode === 'reject' ? 'error' : 'primary'"
                  depressed
                  rounded
                  class="text-none font-weight-bold wd-action-btn"
                  :loading="acting"
                  @click="confirmReason"
                >
                  {{ $t("confirm") }}
                </v-btn>
              </div>
            </div>
          </template>

          <template v-else>
            <v-spacer />
            <div class="wd-footer-btn-group">
              <v-btn
                v-if="canReverse"
                outlined
                rounded
                color="error"
                class="text-none font-weight-bold wd-action-btn"
                @click="startReason('cancel')"
              >
                <v-icon left small>fal fa-undo</v-icon>
                {{ $t("waste_action_reverse") }}
              </v-btn>

              <v-btn
                v-if="canDecide"
                outlined
                rounded
                color="error"
                class="text-none font-weight-bold wd-action-btn"
                :disabled="acting"
                @click="startReason('reject')"
              >
                <v-icon left small>fal fa-times</v-icon>
                {{ $t("reject") }}
              </v-btn>

              <v-btn
                v-if="canDecide"
                depressed
                rounded
                color="primary"
                class="text-none font-weight-bold wd-action-btn wd-action-btn--primary"
                :loading="acting"
                @click="approve"
              >
                <v-icon left small>fal fa-check</v-icon>
                {{ $t("waste_action_approve") }}
              </v-btn>
            </div>
          </template>
        </div>
      </template>
    </CustomDialog>

    <v-dialog v-model="lightbox" max-width="900">
      <v-card class="wd-lightbox">
        <v-btn icon class="wd-lightbox__close" :aria-label="$t('close')" @click="lightbox = false">
          <v-icon small>fal fa-times</v-icon>
        </v-btn>
        <v-img :src="lightboxSrc" contain max-height="80vh" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  wasteMixin,
  WASTE_MAX_IMAGES,
  WASTE_MAX_IMAGE_MB,
  WASTE_IMAGE_TYPES,
} from "@/mixin/waste";
import WasteStatusPill from "@/components/waste/WasteStatusPill";
import WasteItemTypeTag from "@/components/waste/WasteItemTypeTag";
import CustomDialog from "@/components/customDialog.vue";
import { confirm, success, toast } from "@/controllers/alert";

const TYPE_ICONS = {
  simple: "fal fa-box",
  variant: "fal fa-layer-group",
  recipe: "fal fa-utensils",
  ingredient: "fal fa-flask",
};

export default {
  name: "WasteDetailDialog",

  mixins: [wasteMixin],

  components: { WasteStatusPill, WasteItemTypeTag, CustomDialog },

  props: {
    value: { type: Boolean, default: false },
    wasteId: { type: [Number, String], default: null },
    canApprove: { type: Boolean, default: false },
    userId: { type: [Number, String], default: null },
  },

  data: () => ({
    loadSeq: 0,
    loading: false,
    waste: null,
    expanded: {},
    mode: null,
    reasonText: "",
    reasonError: false,
    acting: false,
    uploading: false,
    photoError: "",
    lightbox: false,
    lightboxSrc: "",
  }),

  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    isOwner() {
      return !!this.waste && String(this.waste.user_id) === String(this.userId);
    },

    isPending() {
      return !!this.waste && this.waste.status === "pending";
    },

    canEditPhotos() {
      return this.isPending && (this.isOwner || this.canApprove);
    },

    canDecide() {
      return this.isPending && this.canApprove;
    },

    // a pending request can be withdrawn by whoever filed it
    canWithdraw() {
      return this.isPending && this.isOwner;
    },

    // an approved one can only be undone by an approver, and a reversal
    // document is never undone again
    canReverse() {
      return (
        !!this.waste &&
        this.waste.status === "approved" &&
        this.waste.doc_type !== "reversal" &&
        this.canApprove
      );
    },

    showActions() {
      return this.canDecide || this.canReverse || !!this.mode;
    },

    // What happened to this request, oldest first.
    timeline() {
      const w = this.waste;
      if (!w) return [];

      const events = [
        {
          key: "submitted",
          tone: "info",
          title: this.$t("waste_timeline_submitted"),
          who: this.wasteUserName(w.requested_by),
          at: this.wasteDate(w.submitted_at || w.created_at),
        },
      ];

      // approved_by/approved_at are filled by an approval AND by a rejection
      if (w.approved_by_user && w.approved_at) {
        const rejected = w.status === "rejected";
        events.push({
          key: "decision",
          tone: rejected ? "error" : "success",
          title: this.$t(rejected ? "rejected" : "approved"),
          who: this.wasteUserName(w.approved_by_user),
          at: this.wasteDate(w.approved_at),
        });
      }

      if (w.status === "cancelled") {
        events.push({
          key: "cancelled",
          tone: "muted",
          title: this.$t("cancelled"),
          who: this.wasteUserName(w.cancelled_by_user),
          at: this.wasteDate(w.cancelled_at),
        });
      }

      if (w.status === "pending") {
        events.push({
          key: "waiting",
          tone: "warning",
          title: this.$t("waste_timeline_waiting"),
          who: "",
          at: "",
        });
      }

      // only the last step is "where the request is now"; the ones before it are done
      return events.map((event, index) => ({ ...event, current: index === events.length - 1 }));
    },
  },

  watch: {
    value(isOpen) {
      if (isOpen) this.load();
    },
  },

  methods: {
    docTypeLabel(type) {
      return this.$t(`waste_doc_${type}`);
    },

    typeIcon(type) {
      return TYPE_ICONS[type] || TYPE_ICONS.simple;
    },

    // Colour of the current step's dot, straight from the live Vuetify theme so it
    // follows light/dark mode. (The --v-*-base CSS variables are not defined in
    // this app, so a stylesheet cannot reach those colours.) "muted" has no theme
    // colour and keeps the neutral CSS default.
    dotStyle(event) {
      if (!event.current) return {};
      const name = { info: "primary", success: "success", error: "error", warning: "warning" }[event.tone];
      const color = name && this.$vuetify.theme.currentTheme[name];
      return color ? { background: color } : {};
    },

    // unit names come in four languages (product_sizes.name / name_en / ...)
    unitName(unit) {
      if (!unit) return "";
      const locale = this.$i18n.locale;
      if (locale === "en" && unit.name_en) return unit.name_en;
      if (locale === "th" && unit.name_th) return unit.name_th;
      if ((locale === "cn" || locale === "ch") && unit.name_cn) return unit.name_cn;
      return unit.name || unit.name_en || "";
    },

    // Once approved the real stock after the change is stored; until then show
    // what it will be (stock at request time + the planned effect), flagged as
    // an estimate so nobody reads it as already done.
    stockAfter(line) {
      if (line.current_stock !== null && line.current_stock !== undefined) {
        return { value: line.current_stock, estimate: false };
      }
      // only a request still waiting for a decision is going to change stock
      if (this.waste && this.waste.status === "pending") {
        return {
          value: Math.round((Number(line.last_stock) + Number(line.stock_effect || 0)) * 1000) / 1000,
          estimate: true,
        };
      }
      return { value: null, estimate: false };
    },

    async load() {
      // opening A then B quickly: only the latest request may fill the dialog, or
      // the approve button would act on A while B is on screen
      const seq = ++this.loadSeq;
      this.waste = null;
      this.expanded = {};
      this.mode = null;
      this.reasonText = "";
      this.reasonError = false;
      this.photoError = "";
      if (!this.wasteId) return;

      this.loading = true;
      try {
        const { data } = await this.$http.get(`stock_waste/${this.wasteId}`);
        if (seq !== this.loadSeq) return;
        if (data.status) this.waste = data.data;
      } catch (err) {
        if (seq !== this.loadSeq) return;
        // the interceptor stays quiet on a 404, so say why the dialog is closing
        if (err && (err.code === 404 || (err.response && err.response.status === 404))) {
          toast(this, "waste_not_found", "error");
        }
        this.open = false;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },

    preview(image) {
      this.lightboxSrc = this.wasteImageSrc(image.file_path);
      this.lightbox = true;
    },

    startReason(mode) {
      this.mode = mode;
      this.reasonText = "";
      this.reasonError = false;
    },

    // Posts a decision and swaps in the fresh document the API returns.
    async run(url, payload, message) {
      this.acting = true;
      try {
        const { data } = await this.$http.post(url, payload);
        if (data.status) {
          this.waste = data.data;
          this.mode = null;
          this.$emit("changed");
          await success(this, message || data.message);
        }
      } catch (err) {
        // the axios interceptor already showed the API's message
      } finally {
        this.acting = false;
      }
    },

    async approve() {
      const { isConfirmed } = await confirm({ props: this, text: "waste_confirm_approve" });
      if (!isConfirmed) return;
      await this.run(`stock_waste_action/${this.waste.id}`, { status: "approved" });
    },

    async confirmReason() {
      const reason = (this.reasonText || "").trim();
      if (!reason) {
        this.reasonError = true;
        toast(
          this,
          this.mode === "reject" ? "reject_reason_required" : "waste_cancel_reason_required",
          "warning"
        );
        return;
      }

      if (this.mode === "reject") {
        const { isConfirmed } = await confirm({ props: this, text: "waste_confirm_reject" });
        if (!isConfirmed) return;
        await this.run(`stock_waste_action/${this.waste.id}`, {
          status: "rejected",
          reject_reason: reason,
        });
        return;
      }

      const { isConfirmed } = await confirm({
        props: this,
        text: this.waste.status === "approved" ? "waste_confirm_reverse" : "waste_confirm_withdraw",
      });
      if (!isConfirmed) return;
      await this.run(`stock_waste_cancel/${this.waste.id}`, { reason });
    },

    async onAddPhotos(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      this.photoError = "";
      if (!files.length) return;

      const bad = files.find((file) => !WASTE_IMAGE_TYPES.includes(file.type));
      if (bad) {
        this.photoError = this.$t("waste_photo_type_invalid", { name: bad.name });
        return;
      }
      const big = files.find((file) => file.size > WASTE_MAX_IMAGE_MB * 1024 * 1024);
      if (big) {
        this.photoError = this.$t("waste_photo_too_large", {
          name: big.name,
          size: WASTE_MAX_IMAGE_MB,
        });
        return;
      }
      if (this.waste.images.length + files.length > WASTE_MAX_IMAGES) {
        this.photoError = this.$t("waste_photo_limit", { max: WASTE_MAX_IMAGES });
        return;
      }

      const form = new FormData();
      files.forEach((file) => form.append("images[]", file));

      this.uploading = true;
      try {
        const { data } = await this.$http.post(`stock_waste_images/${this.waste.id}`, form);
        if (data.status) {
          this.waste = data.data;
          this.$emit("changed");
        }
      } catch (err) {
        // the axios interceptor already showed the API's message
      } finally {
        this.uploading = false;
      }
    },

    async removePhoto(image) {
      const { isConfirmed } = await confirm({ props: this, text: "waste_confirm_delete_photo" });
      if (!isConfirmed) return;

      try {
        const { data } = await this.$http.delete(
          `stock_waste_images/${this.waste.id}/${image.id}`
        );
        if (data.status) {
          this.waste = data.data;
          this.$emit("changed");
        }
      } catch (err) {
        // the axios interceptor already showed the API's message
      }
    },
  },
};
</script>

<style scoped>
/* ==========================================================================
   WASTE DETAIL DIALOG (UI/UX PRO MAX REDESIGN)
   Clean, modern enterprise POS aesthetics with slate neutrals, refined accents,
   micro-interactions, and accessible tabular typography.
   ========================================================================== */

/* ---- Dialog Header ---- */
.wd-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.wd-header-icon-box {
  display: inline-flex;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #ede9fe;
  color: #7c3aed;
  box-shadow: 0 1px 2px rgba(124, 58, 237, 0.08);
}

.wd-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.wd-header-eyebrow {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.2;
}

.wd-header-code-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.wd-header-code {
  color: #0f172a;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.wd-header-badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wd-pill-doc {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.wd-header-skeleton {
  width: 220px;
}

/* ---- Body Container ---- */
.waste-detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ---- Banner Alerts / Notes ---- */
.wd-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.5;
  border: 1px solid transparent;
}

.wd-alert__icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.wd-alert__body {
  flex: 1;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.wd-alert--error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.wd-alert--warning {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.wd-alert--info {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.wd-alert--neutral {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #334155;
}

/* ---- KPIs Metric Row ---- */
.wd-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.wd-kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.wd-kpi-card:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.wd-kpi-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex: 0 0 44px;
}

.wd-kpi-card__icon--indigo {
  background: #eef2ff;
  color: #4f46e5;
}

.wd-kpi-card__icon--rose {
  background: #fff1f2;
  color: #e11d48;
}

.wd-kpi-card__icon--amber {
  background: #fffbeb;
  color: #d97706;
}

.wd-kpi-card__content {
  min-width: 0;
  flex: 1;
}

.wd-kpi-card__label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 2px;
}

.wd-kpi-card__val {
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

.text-rose {
  color: #e11d48 !important;
}

.text-amber {
  color: #d97706 !important;
}

/* ---- Two-Column Grid: Timeline & Details ---- */
.wd-two-col-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.wd-panel {
  display: flex;
  flex-direction: column;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.wd-panel__header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
}

.wd-panel__title {
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.wd-panel__body {
  flex: 1;
}

/* Timeline */
.wd-timeline {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.wd-timeline__item {
  position: relative;
  display: flex;
  gap: 14px;
  padding-bottom: 18px;
}

.wd-timeline__item:last-child {
  padding-bottom: 0;
}

.wd-timeline__item:not(:last-child)::before {
  position: absolute;
  top: 18px;
  bottom: 0;
  left: 7px;
  width: 2px;
  background: #e2e8f0;
  content: "";
}

.wd-timeline__dot {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  border-radius: 50%;
  background: #f8fafc;
  box-shadow: 0 0 0 1.5px #cbd5e1;
}

.wd-timeline__tick {
  display: block;
}

.wd-timeline__item.is-done .wd-timeline__tick {
  color: #64748b;
}

.wd-timeline__item.is-current .wd-timeline__tick {
  color: #ffffff;
}

.wd-timeline__item.is-current .wd-timeline__dot {
  background: #64748b;
  box-shadow: 0 0 0 4px #e2e8f0;
}

.wd-timeline__content {
  flex: 1;
  min-width: 0;
}

.wd-timeline__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.wd-timeline__title {
  color: #0f172a;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.3;
}

.wd-timeline__item.is-done .wd-timeline__title {
  color: #475569;
  font-weight: 600;
}

.wd-timeline__badge {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-size: 0.6875rem;
  font-weight: 700;
}

.wd-timeline__meta {
  color: #64748b;
  font-size: 0.75rem;
  margin-top: 2px;
  line-height: 1.4;
}

/* Info Details */
.wd-info-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
}

.wd-info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.wd-info-item--full {
  grid-column: 1 / -1;
}

.wd-info-item__label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
}

.wd-info-item__val {
  display: flex;
  align-items: center;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
}

.wd-info-item__note {
  margin: 4px 0 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre-wrap;
  font-weight: 500;
}

/* ---- Section Common ---- */
.wd-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wd-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.wd-section__title-group {
  display: flex;
  align-items: center;
}

.wd-section__title {
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.wd-counter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.6875rem;
  font-weight: 800;
}

.wd-hidden-input {
  display: none;
}

/* Evidence Gallery */
.wd-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 12px;
}

.wd-gallery__item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.wd-gallery__open {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.wd-gallery__open img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.wd-gallery__hover-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.35);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.wd-gallery__open:hover img,
.wd-gallery__open:focus-visible img {
  transform: scale(1.06);
}

.wd-gallery__open:hover .wd-gallery__hover-overlay,
.wd-gallery__open:focus-visible .wd-gallery__hover-overlay {
  opacity: 1;
}

.wd-gallery__remove {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
  border: 1px solid #fecaca;
  border-radius: 50%;
  background: #ffffff;
  color: #ef4444;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  transition: background-color 0.15s ease, transform 0.15s ease;
  z-index: 2;
}

.wd-gallery__remove:hover {
  background: #fee2e2;
  transform: scale(1.08);
}

.wd-empty-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 1.5px dashed #e2e8f0;
  border-radius: 14px;
  color: #94a3b8;
  font-size: 0.8125rem;
  font-weight: 600;
  background: #fafafa;
}

/* ---- Line Items List ---- */
.wd-lines-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wd-line-card {
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: border-color 0.15s ease;
}

.wd-line-card:hover {
  border-color: #cbd5e1;
}

.wd-line-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.wd-line-card__icon-badge {
  display: inline-flex;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.wd-line-card--variant .wd-line-card__icon-badge {
  background: #ede9fe;
  color: #7c3aed;
}

.wd-line-card--recipe .wd-line-card__icon-badge {
  background: #fef3c7;
  color: #d97706;
}

.wd-line-card--ingredient .wd-line-card__icon-badge {
  background: #dcfce7;
  color: #16a34a;
}

.wd-line-card__title-wrap {
  flex: 1;
  min-width: 0;
}

.wd-line-card__name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.wd-line-card__name {
  color: #0f172a;
  font-size: 0.9375rem;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.wd-chip-variant {
  display: inline-flex;
  padding: 1px 8px;
  border-radius: 6px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.6875rem;
  font-weight: 700;
}

.wd-line-card__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.wd-chip-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.6875rem;
  font-weight: 700;
}

.wd-chip-tag--in {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.wd-chip-sku {
  display: inline-flex;
  align-items: center;
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.6875rem;
  font-weight: 600;
}

.wd-line-card__cost-block {
  flex: 0 0 auto;
  text-align: right;
}

.wd-line-card__cost-amount {
  color: #0f172a;
  font-size: 1.125rem;
  font-weight: 800;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

.wd-line-card__cost-caption {
  color: #64748b;
  font-size: 0.6875rem;
  font-weight: 600;
  margin-top: 2px;
}

/* Metrics Row inside line */
.wd-line-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px 14px;
  margin-top: 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
}

.wd-metric-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.wd-metric-label {
  color: #64748b;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.wd-metric-val {
  color: #0f172a;
  font-size: 0.875rem;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}

.wd-metric-unit {
  margin-left: 2px;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
}

.wd-metric-sub {
  color: #64748b;
  font-size: 0.6875rem;
  font-weight: 500;
  margin-top: 1px;
}

.wd-estimate-text {
  font-style: italic;
  color: #d97706;
}

.wd-line-comment {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  color: #334155;
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* Recipe breakdown */
.wd-breakdown-toggle {
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7c3aed;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.wd-breakdown-toggle:hover {
  opacity: 0.8;
}

.wd-breakdown-table {
  margin-top: 10px;
  padding: 8px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
}

.wd-breakdown-table__row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 1.2fr) 90px;
  gap: 12px;
  padding: 8px 0;
  color: #334155;
  font-size: 0.8125rem;
  align-items: center;
}

.wd-breakdown-table__row + .wd-breakdown-table__row {
  border-top: 1px solid #f1f5f9;
}

.wd-breakdown-table__name {
  font-weight: 700;
  color: #0f172a;
  overflow-wrap: anywhere;
}

.wd-breakdown-table__stock {
  color: #64748b;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.wd-breakdown-table__qty {
  text-align: right;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.wd-breakdown-table__cost {
  text-align: right;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

/* ---- Action Footer ---- */
.wd-footer-bar {
  display: flex;
  align-items: center;
  flex-grow: 1;
  width: 100%;
}

.wd-footer-reason-mode {
  display: flex;
  align-items: center;
  flex-grow: 1;
  gap: 12px;
  flex-wrap: wrap;
}

.wd-footer-textarea {
  min-width: 260px;
}

.wd-footer-btn-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.wd-action-btn {
  height: 42px !important;
  min-width: 100px !important;
  padding: 0 18px !important;
  font-size: 0.875rem !important;
  letter-spacing: normal !important;
  border-radius: 12px !important;
}

.wd-action-btn--primary {
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.25) !important;
}

.wd-btn-cancel,
.wd-btn-withdraw {
  height: 42px !important;
  padding: 0 16px !important;
  color: #64748b !important;
}

.wd-lightbox {
  position: relative;
  background: #0f172a;
}

.wd-lightbox__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9) !important;
  color: #0f172a !important;
}

/* ---- Utility Classes ---- */
.text-slate-300 {
  color: #cbd5e1 !important;
}

.text-slate-400 {
  color: #94a3b8 !important;
}

.text-slate-500 {
  color: #64748b !important;
}

.text-slate-600 {
  color: #475569 !important;
}

.text-slate-800 {
  color: #1e293b !important;
}

.text-success {
  color: #16a34a !important;
}

/* ---- Responsive Adjustments ---- */
@media (max-width: 768px) {
  .wd-kpis {
    grid-template-columns: 1fr;
  }

  .wd-two-col-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .wd-header-content {
    flex-wrap: wrap;
  }

  .wd-line-card__header {
    flex-wrap: wrap;
  }

  .wd-line-card__cost-block {
    width: 100%;
    text-align: left;
    margin-top: 6px;
  }

  .wd-breakdown-table__row {
    grid-template-columns: minmax(0, 1fr) auto auto;
  }

  .wd-breakdown-table__row > :nth-child(2) {
    display: none;
  }

  .wd-footer-reason-mode {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wd-gallery__open img {
    transition: none;
  }
  .wd-kpi-card {
    transition: none;
  }
}
</style>

